import { UpdateDropdownsRequest, UpdateDropdownsResponse } from 'pages/pnl/api';
import http from 'api/httpClient';
export type LhcRouteVendorsPayload = LhcRouteDetailsPayload & {
  vendor_type?: 'vehicle_owner' | 'broker';
};

export type LhcDropdownLevel = {
  id: number;
  name: string;
  options: { ou_code: string; ou_name: string }[];
  selected?: string[];
};

export type FetchLHCDropdownsResponse = {
  levels: LhcDropdownLevel[];
};

type PaginationInfo = {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

export interface LhcSummaryRecord {
  origin: string;
  destination: string;
  vehicle_type: string;
  total_count: number;
  min_value: number;
  max_value: number;
  avg_value: number;
}

export interface LhcRouteDetailRecord {
  lhc_number: string;
  lhc_date: string | null;
  vehicle_number: string;
  branch_name: string;
  origin: string;
  destination: string;
  lhc_distance: number;
  lorry_hire_expenses: number;
  lhc_total_act_wt: number;
  lhc_total_chr_wt: number;
  vehicle_loading_capacity: number;
  utilization_percent: number | null;
  rkm: number | null;
  rtkm: number | null;
  // Category fields (present on graph-mode rows; used for the graph "Color By")
  lhc_category?: string | null;
  va_category?: string | null;
  vendor_type?: string | null;
  // Present on graph-mode rows: expense is >±10% from the median (drawn red).
  is_outlier?: boolean;
}

export type LhcRecord = LhcSummaryRecord | LhcRouteDetailRecord;

export interface LhcStats extends LhcSummaryRecord {
  min_count: number;
  max_count: number;
  avg_count: number;
  outlier_count: number;
}

export type RouteLevel = 'state' | 'district' | 'city' | 'location';

export type LhcRouteDetailsPayload = {
  origin: string;
  destination: string;
  route_by: string;
  // For flexible routes (e.g. State -> City) each side can match at its own
  // level; when omitted the backend falls back to route_by for both.
  origin_type?: RouteLevel;
  destination_type?: RouteLevel;
  vehicle_type: string;
  lorry_hire_expenses?: number;
  outliers?: boolean;
  year?: string;
  filters?: Record<string, string[]>;
  dateFrom?: string | null;
  dateTo?: string | null;
  lhcDropdownLevels: { id: number; value: string | null }[];
  page?: number;
  pageSize?: number;
  sort?: { by: string; dir: 'asc' | 'desc' };
  viewMode?: 'table' | 'graph' | 'vendors';
};

export type LhcRouteDetailsResponse = {
  data: LhcRecord[];
  pagination: PaginationInfo;
  stats?: {
    min_count?: number;
    max_count?: number;
    avg_count?: number;
    min_value?: number;
    max_value?: number;
    avg_value?: number;
    outlier_count?: number;
  };
};

export type FetchLhcYearsResponse = {
  years: string[];
};

export async function fetchLhcYears(): Promise<FetchLhcYearsResponse> {
  // years were folded into the shared periods feature: GET /api/v1/periods/years?source=lhc
  return http.get('/api/v1/periods/years?source=lhc');
}

export async function updateLhcDropdowns(payload: UpdateDropdownsRequest): Promise<UpdateDropdownsResponse> {
  return http.post('/api/v1/lhc/update-dropdowns', payload);
}


export async function fetchLhcDropdowns(payload?: any): Promise<FetchLHCDropdownsResponse> {
  return http.post('/api/v1/lhc/update-lhc-dropdowns', payload || {});
}


export async function fetchLHCData(payload?: any) {
  return http.post('/api/v1/lhc/fetch-data', payload);
}

export async function fetchLhcRouteDetails(payload: LhcRouteDetailsPayload) {
  return http.post('/api/v1/lhc/fetch-lhc-route-details', payload);
}

// --- Route Search (typeahead) ---
export type RouteGeoSuggestion = {
  level: RouteLevel;
  value: string;
  label: string;
  subtitle: string;
};

export type RouteGeoSuggestPayload = {
  side: 'origin' | 'destination';
  q: string;
  year?: string;
  filters?: Record<string, string[]>;
  dateFrom?: string | null;
  dateTo?: string | null;
  lhcDropdownLevels: { id: number; value: string | null }[];
  limitPerLevel?: number;
};

export async function suggestRouteGeography(
  payload: RouteGeoSuggestPayload
): Promise<{ suggestions: RouteGeoSuggestion[] }> {
  return http.post('/api/v1/lhc/suggest-route-geography', payload);
}

export type RouteVehicleTypesPayload = {
  origin: string;
  destination: string;
  origin_type: RouteLevel;
  destination_type: RouteLevel;
  year?: string;
  filters?: Record<string, string[]>;
  dateFrom?: string | null;
  dateTo?: string | null;
  lhcDropdownLevels: { id: number; value: string | null }[];
};

export async function fetchRouteVehicleTypes(
  payload: RouteVehicleTypesPayload
): Promise<{ vehicle_types: string[] }> {
  return http.post('/api/v1/lhc/route-vehicle-types', payload);
}


export interface VendorRecord {
  vendor_code: string;
  vendor_name: string;
  vendor_type: string | null;
  lhc_count: number;
  total_value: number;
  avg_value: number;
  min_value: number;
  max_value: number;
  concentration_percent: number;
}

export interface VendorStats {
  vehicle_owner_count: number;
  broker_count: number;
  vehicle_owner_avg_rate: number;
  broker_avg_rate: number;
}

export interface VendorResponse {
  data: VendorRecord[];
  stats: VendorStats;
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export async function fetchLhcRouteVendors(payload: LhcRouteVendorsPayload): Promise<VendorResponse> {
  return http.post('/api/v1/lhc/fetch-lhc-route-vendors', payload);
}